import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { Label } from '@components/Label';
import { type SubscriptionModel } from '@models/SubscriptionModel';
import { AppLogin } from '../../workflow/connectionMgmt/detail/appLogin';

type Props = {
  subscription: SubscriptionModel;
};
export const ConnectionStatusLabel = ({ subscription }: Props) => {
  const navigate = useNavigate();
  const [appLoginModalState, setAppLoginModalState] = useState<{ isOpen: boolean; type: 'new' | 'modify' }>({
    isOpen: false,
    type: 'new',
  });
  const { t } = useTranslation();

  const hasConnection = !!subscription.connection;

  const navigateToConectionDetail = () => {
    if (hasConnection) {
      navigate(`../connections/${subscription.connection.connectionId}/connection`);
    }
  };

  const handleFailedClick = () => {
    setAppLoginModalState({ isOpen: true, type: 'modify' });
  };

  const handleUnconnectedClick = () => {
    setAppLoginModalState({ isOpen: true, type: 'new' });
  };

  const handleAppLoginClose = () => {
    setAppLoginModalState({ isOpen: false, type: 'new' });
  };

  return (
    <>
      {hasConnection ? (
        <>
          {subscription.connection.status === 'CONNECTED' && (
            <Tooltip arrow placement="bottom-start" title={t('SubscriptionDetail.connection.connectedTooltip')}>
              <Label variant="contain" color="green" role="button" onClick={navigateToConectionDetail}>
                {t('SubscriptionDetail.connection.connectedLabel')}
              </Label>
            </Tooltip>
          )}
          {subscription.connection.status === 'FAILED' && (
            <Tooltip arrow placement="bottom-start" title={t('SubscriptionDetail.connection.failedTooltip')}>
              <Label variant="contain" color="red" role="button" onClick={handleFailedClick}>
                {t('SubscriptionDetail.connection.failedLabel')}
              </Label>
            </Tooltip>
          )}
          {subscription.connection.status === 'DISCONNECTED' && (
            <Tooltip arrow placement="bottom-start" title={t('SubscriptionDetail.connection.disconnectedTooltip')}>
              <DisconnectedLabel variant="contain" role="button" color="gray" onClick={navigateToConectionDetail}>
                {t('SubscriptionDetail.connection.disconnectedLabel')}
              </DisconnectedLabel>
            </Tooltip>
          )}
          {subscription.connection.status === 'CONNECTING' && (
            <Tooltip arrow placement="bottom-start" title={t('SubscriptionDetail.connection.connectingTooltip')}>
              <Label variant="solid" role="button" color="gradient" onClick={navigateToConectionDetail}>
                {t('SubscriptionDetail.connection.connectingLabel')}
              </Label>
            </Tooltip>
          )}
        </>
      ) : (
        <Tooltip arrow placement="bottom-start" title={t('SubscriptionDetail.connection.noneTooltip')}>
          <UnconnectedLabel variant="contain" role="button" color="gray" onClick={handleUnconnectedClick}>
            {t('SubscriptionDetail.connection.noneLabel')}
          </UnconnectedLabel>
        </Tooltip>
      )}
      <AppLogin
        subscription={subscription}
        open={appLoginModalState.isOpen}
        onClose={handleAppLoginClose}
        type={appLoginModalState.type}
      />
    </>
  );
};

const DisconnectedLabel = styled(Label)`
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  background-color: ${({ theme: { colors } }) => colors['bg-gray-light-m']};
`;

const UnconnectedLabel = styled(Label)`
  color: ${({ theme: { colors } }) => colors['text-white']};
  background-color: ${({ theme: { colors } }) => colors['bg-gray-dark-50']};
`;
